import type { KeyboardEvent, MouseEvent } from 'react';
import React, { useEffect } from 'react';

import Tooltip from '../../../../Tooltip/Tooltip';
import { useCardContext } from '../../CardContext';
import { Variant } from './types';
import { StyledBookmarkIcon, Wrapper } from './FavoriteButton.styled';

export interface FavoriteButtonProps {
  dataCy?: string;
  onAction: () => void;
  tooltipText: string;
  variant: Variant;
  tooltipReferencePortalNode?: HTMLElement | null;
}

export const FavoriteButton = ({
  tooltipText,
  dataCy,
  variant,
  onAction,
  tooltipReferencePortalNode,
}: FavoriteButtonProps) => {
  const { setIsFavorite } = useCardContext();

  useEffect(() => {
    setIsFavorite(variant === Variant.FAVED);
  }, [setIsFavorite, variant]);

  const preventDefaultAction = (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    preventDefaultAction(event);
    onAction();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      preventDefaultAction(event);
      onAction();
    }
  };

  return (
    <Tooltip text={tooltipText} renderReferencePortalNode={tooltipReferencePortalNode}>
      <Wrapper
        data-cy={dataCy}
        role="button"
        tabIndex={0}
        aria-label={tooltipText}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        $variant={variant}
      >
        <StyledBookmarkIcon $variant={variant} aria-hidden />
      </Wrapper>
    </Tooltip>
  );
};

FavoriteButton.Variant = Variant;
